import { useState, useEffect } from 'react'
import {
  X,
  Settings,
  Bell,
  FileText,
  MessageSquare,
  Activity,
  Trash2,
  Plus,
  Zap,
} from 'lucide-react'
import { Button } from '../components'
import { AlertProfileSelector, Toggle } from '@roselabs-io/saas-shell'
import type { AlertProfile } from '@roselabs-io/saas-shell'
import type { App, AppUpdate, MetricAlert, MetricCondition } from '../types'
import { appsApi } from '../api/apps'
import { notificationsApi } from '../api/notifications'
import { metricsApi } from '../api/metrics'
import { useAuth } from '../lib/auth'

// Helper to check if condition is anomaly-based
const isAnomalyCondition = (condition: MetricCondition): boolean => condition.startsWith('anomaly_')

// Display labels for conditions
const CONDITION_LABELS: Record<MetricCondition, string> = {
  gt: 'Greater than',
  gte: 'Greater than or equal',
  lt: 'Less than',
  lte: 'Less than or equal',
  eq: 'Equal to',
  anomaly_low: 'Anomaly (Low+)',
  anomaly_medium: 'Anomaly (Medium+)',
  anomaly_high: 'Anomaly (High+)',
  anomaly_critical: 'Anomaly (Critical)',
}

interface AppSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  app: App
  onSubmit: (id: string, data: AppUpdate) => Promise<void>
}

export default function AppSettingsModal({
  isOpen,
  onClose,
  app,
  onSubmit,
}: AppSettingsModalProps) {
  const { teamId } = useAuth()

  // Basic settings
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [healthUrl, setHealthUrl] = useState('')
  const [metricsUrl, setMetricsUrl] = useState('')
  const [pingInterval, setPingInterval] = useState(5)
  const [metricsInterval, setMetricsInterval] = useState<number | null>(5)
  const [isActive, setIsActive] = useState(true)

  // Alert settings
  const [alertOnError, setAlertOnError] = useState(true)
  const [alertOnPingFailure, setAlertOnPingFailure] = useState(true)
  const [alertOnRecovery, setAlertOnRecovery] = useState(true)
  const [alertOnRegression, setAlertOnRegression] = useState(true)
  const [alertOnAnomaly, setAlertOnAnomaly] = useState(true)
  const [autoResolveDays, setAutoResolveDays] = useState<number | null>(null)
  const [alertProfileId, setAlertProfileId] = useState<string | null>(null)
  const [alertProfiles, setAlertProfiles] = useState<AlertProfile[]>([])

  // Environment filtering
  const [alertEnvironments, setAlertEnvironments] = useState<string[]>([])
  const [reportEnvironments, setReportEnvironments] = useState<string[]>([])
  const [availableEnvironments, setAvailableEnvironments] = useState<string[]>([])

  // Channel overrides
  const [slackChannelOverride, setSlackChannelOverride] = useState('')
  const [webhookUrlOverride, setWebhookUrlOverride] = useState('')
  const [discordWebhookOverride, setDiscordWebhookOverride] = useState('')

  // Metric alerts
  const [metricAlerts, setMetricAlerts] = useState<MetricAlert[]>([])
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false)
  const [showAddAlert, setShowAddAlert] = useState(false)
  const [newAlertMetric, setNewAlertMetric] = useState('')
  const [newAlertCondition, setNewAlertCondition] = useState<MetricCondition>('gt')
  const [newAlertThreshold, setNewAlertThreshold] = useState<string>('')
  const [availableMetrics, setAvailableMetrics] = useState<string[]>([])

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Populate form when app changes
  useEffect(() => {
    if (app) {
      setName(app.name)
      setDescription(app.description || '')
      setHealthUrl(app.health_url || '')
      setMetricsUrl(app.metrics_url || '')
      setPingInterval(app.ping_interval_minutes)
      setMetricsInterval(app.metrics_interval_minutes)
      setIsActive(app.is_active)
      setAlertOnError(app.alert_on_error)
      setAlertOnPingFailure(app.alert_on_ping_failure)
      setAlertOnRecovery(app.alert_on_recovery)
      setAlertOnRegression(app.alert_on_regression)
      setAlertOnAnomaly(app.alert_on_anomaly)
      setAutoResolveDays(app.auto_resolve_days)
      setAlertProfileId(app.alert_profile_id)
      setAlertEnvironments(app.alert_environments || ['production'])
      setReportEnvironments(app.report_environments || ['production'])
      setSlackChannelOverride(app.alert_slack_channel_override || '')
      setWebhookUrlOverride(app.alert_webhook_url_override || '')
      setDiscordWebhookOverride(app.alert_discord_webhook_override || '')
    }
  }, [app])

  // Fetch alert profiles
  useEffect(() => {
    if (isOpen && teamId) {
      notificationsApi.listAlertProfiles(teamId).then(setAlertProfiles).catch(console.error)
    }
  }, [isOpen, teamId])

  // Fetch available environments
  useEffect(() => {
    if (isOpen) {
      appsApi.getEnvironments().then(setAvailableEnvironments).catch(console.error)
    }
  }, [isOpen])

  // Fetch metric alerts and available metrics
  useEffect(() => {
    if (isOpen && app) {
      setIsLoadingAlerts(true)
      Promise.all([metricsApi.listAlerts(app.id), metricsApi.getAvailable(app.id)])
        .then(([alertsRes, metricsRes]) => {
          setMetricAlerts(alertsRes.alerts)
          setAvailableMetrics(metricsRes.metrics || [])
        })
        .catch(console.error)
        .finally(() => setIsLoadingAlerts(false))
    }
  }, [isOpen, app])

  const handleAddMetricAlert = async () => {
    if (!app || !newAlertMetric) return
    try {
      const isAnomaly = isAnomalyCondition(newAlertCondition)
      const alert = await metricsApi.createAlert(app.id, {
        metric_name: newAlertMetric,
        condition: newAlertCondition,
        threshold: isAnomaly ? null : parseFloat(newAlertThreshold),
      })
      setMetricAlerts(prev => [...prev, alert])
      setShowAddAlert(false)
      setNewAlertMetric('')
      setNewAlertCondition('gt')
      setNewAlertThreshold('')
    } catch (err) {
      console.error('Failed to create metric alert:', err)
    }
  }

  const handleToggleMetricAlert = async (alertId: string, isActive: boolean) => {
    if (!app) return
    try {
      const updated = await metricsApi.updateAlert(app.id, alertId, { is_active: isActive })
      setMetricAlerts(prev => prev.map(a => (a.id === alertId ? updated : a)))
    } catch (err) {
      console.error('Failed to update metric alert:', err)
    }
  }

  const handleDeleteMetricAlert = async (alertId: string) => {
    if (!app) return
    try {
      await metricsApi.deleteAlert(app.id, alertId)
      setMetricAlerts(prev => prev.filter(a => a.id !== alertId))
    } catch (err) {
      console.error('Failed to delete metric alert:', err)
    }
  }

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    setIsSubmitting(true)
    try {
      await onSubmit(app.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        health_url: healthUrl.trim() || undefined,
        metrics_url: metricsUrl.trim() || undefined,
        ping_interval_minutes: pingInterval,
        metrics_interval_minutes: metricsInterval,
        is_active: isActive,
        alert_on_error: alertOnError,
        alert_on_ping_failure: alertOnPingFailure,
        alert_on_recovery: alertOnRecovery,
        alert_on_regression: alertOnRegression,
        alert_on_anomaly: alertOnAnomaly,
        auto_resolve_days: autoResolveDays,
        alert_profile_id: alertProfileId,
        alert_environments: alertEnvironments.length > 0 ? alertEnvironments : [],
        report_environments: reportEnvironments.length > 0 ? reportEnvironments : [],
        alert_slack_channel_override: slackChannelOverride.trim() || null,
        alert_webhook_url_override: webhookUrlOverride.trim() || null,
        alert_discord_webhook_override: discordWebhookOverride.trim() || null,
      })
      onClose()
    } catch {
      // Error handled by context (toast shown)
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleEnvironment = (
    env: string,
    current: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (current.includes(env)) {
      setter(current.filter(e => e !== env))
    } else {
      setter([...current, env])
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">App Settings</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          {/* Basic Settings Section */}
          <div className="p-6 space-y-4 border-b border-slate-200">
            <div className="flex items-center gap-2 text-slate-700 mb-3">
              <Settings className="w-4 h-4" />
              <span className="font-medium">Basic Settings</span>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">App Name *</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Health Check URL
              </label>
              <input
                type="url"
                value={healthUrl}
                onChange={e => setHealthUrl(e.target.value)}
                placeholder="https://myapp.com/health"
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave empty to disable uptime monitoring
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Metrics URL</label>
              <input
                type="url"
                value={metricsUrl}
                onChange={e => setMetricsUrl(e.target.value)}
                placeholder="https://myapp.com/__falcon/metrics"
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">
                Prometheus-compatible endpoint for custom metrics collection
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Ping Interval (Health Checks)
              </label>
              <select
                value={pingInterval}
                onChange={e => setPingInterval(Number(e.target.value))}
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value={1}>Every 1 minute</option>
                <option value={5}>Every 5 minutes</option>
                <option value={15}>Every 15 minutes</option>
                <option value={30}>Every 30 minutes</option>
                <option value={60}>Every hour</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                How often to check the health URL for uptime monitoring
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Metrics Collection Interval
              </label>
              <select
                value={metricsInterval ?? ''}
                onChange={e => setMetricsInterval(e.target.value ? Number(e.target.value) : null)}
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Disabled</option>
                <option value={1}>Every 1 minute</option>
                <option value={5}>Every 5 minutes</option>
                <option value={15}>Every 15 minutes</option>
                <option value={30}>Every 30 minutes</option>
                <option value={60}>Every hour</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                How often to scrape the metrics URL for custom metrics
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-slate-700">App Active</label>
                <p className="text-xs text-slate-500">Disable to pause all monitoring</p>
              </div>
              <Toggle checked={isActive} onChange={setIsActive} />
            </div>
          </div>

          {/* Alert Settings Section */}
          <div className="p-6 space-y-4 border-b border-slate-200">
            <div className="flex items-center gap-2 text-slate-700 mb-3">
              <Bell className="w-4 h-4" />
              <span className="font-medium">Alert Settings</span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm text-slate-700">Alert on new errors</label>
                <Toggle checked={alertOnError} onChange={setAlertOnError} />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm text-slate-700">Alert on ping failures</label>
                <Toggle checked={alertOnPingFailure} onChange={setAlertOnPingFailure} />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm text-slate-700">Alert on recovery</label>
                <Toggle checked={alertOnRecovery} onChange={setAlertOnRecovery} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-slate-700">Alert on error regression</label>
                  <p className="text-xs text-slate-500">Notify when a resolved error recurs</p>
                </div>
                <Toggle checked={alertOnRegression} onChange={setAlertOnRegression} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-slate-700">Alert on metric anomalies</label>
                  <p className="text-xs text-slate-500">
                    Auto-detect unusual metric patterns using baselines
                  </p>
                </div>
                <Toggle checked={alertOnAnomaly} onChange={setAlertOnAnomaly} />
              </div>
            </div>

            {/* Auto-resolve Setting */}
            <div className="pt-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Auto-resolve errors
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Automatically resolve errors after a period of inactivity
              </p>
              <select
                value={autoResolveDays ?? ''}
                onChange={e => setAutoResolveDays(e.target.value ? Number(e.target.value) : null)}
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Disabled</option>
                <option value={3}>After 3 days</option>
                <option value={7}>After 7 days</option>
                <option value={14}>After 14 days</option>
                <option value={30}>After 30 days</option>
                <option value={60}>After 60 days</option>
                <option value={90}>After 90 days</option>
              </select>
            </div>

            {/* Alert Profile Selector */}
            <div className="pt-2">
              <AlertProfileSelector
                profiles={alertProfiles}
                selectedId={alertProfileId}
                onSelect={setAlertProfileId}
                label="Alert Profile"
                placeholder="Use Team Default"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Controls severity filtering, quiet hours, thresholds, and escalation
              </p>
            </div>

            <div className="pt-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Alert for environments
              </label>
              <p className="text-xs text-slate-500 mb-3">
                Only trigger alerts for errors from selected environments
              </p>
              <div className="flex flex-wrap gap-2">
                {availableEnvironments.map(env => (
                  <button
                    key={env}
                    type="button"
                    onClick={() => toggleEnvironment(env, alertEnvironments, setAlertEnvironments)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      alertEnvironments.includes(env)
                        ? 'bg-accent-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {env}
                  </button>
                ))}
              </div>
              {alertEnvironments.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  No environments selected - alerts are disabled for this app
                </p>
              )}
            </div>
          </div>

          {/* Metric Alerts Section */}
          <div className="p-6 space-y-4 border-b border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-slate-700">
                <Activity className="w-4 h-4" />
                <span className="font-medium">Metric Alerts</span>
              </div>
              <button
                type="button"
                onClick={() => setShowAddAlert(!showAddAlert)}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Alert
              </button>
            </div>
            <p className="text-xs text-slate-500 -mt-2 mb-4">
              Get notified when custom metrics exceed thresholds or when anomalies are detected.
            </p>

            {/* Add new alert form */}
            {showAddAlert && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-3 border border-slate-200">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Metric</label>
                  <select
                    value={newAlertMetric}
                    onChange={e => setNewAlertMetric(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select a metric...</option>
                    {availableMetrics.map(m => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Condition</label>
                  <select
                    value={newAlertCondition}
                    onChange={e => setNewAlertCondition(e.target.value as MetricCondition)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <optgroup label="Threshold-based">
                      <option value="gt">Greater than</option>
                      <option value="gte">Greater than or equal</option>
                      <option value="lt">Less than</option>
                      <option value="lte">Less than or equal</option>
                      <option value="eq">Equal to</option>
                    </optgroup>
                    <optgroup label="Anomaly-based">
                      <option value="anomaly_low">Any anomaly (Low+)</option>
                      <option value="anomaly_medium">Medium+ anomaly</option>
                      <option value="anomaly_high">High+ anomaly</option>
                      <option value="anomaly_critical">Critical anomaly only</option>
                    </optgroup>
                  </select>
                </div>

                {!isAnomalyCondition(newAlertCondition) && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Threshold Value
                    </label>
                    <input
                      type="number"
                      value={newAlertThreshold}
                      onChange={e => setNewAlertThreshold(e.target.value)}
                      placeholder="e.g., 100"
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {isAnomalyCondition(newAlertCondition) && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Anomaly alerts trigger when values deviate significantly from the baseline
                  </p>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddMetricAlert}
                    disabled={
                      !newAlertMetric ||
                      (!isAnomalyCondition(newAlertCondition) && !newAlertThreshold)
                    }
                  >
                    Create Alert
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowAddAlert(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Existing alerts list */}
            {isLoadingAlerts ? (
              <p className="text-sm text-slate-500">Loading alerts...</p>
            ) : metricAlerts.length === 0 ? (
              <p className="text-sm text-slate-500">No metric alerts configured yet.</p>
            ) : (
              <div className="space-y-2">
                {metricAlerts.map(alert => (
                  <div
                    key={alert.id}
                    className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-200"
                  >
                    <div className="flex items-center gap-3">
                      {isAnomalyCondition(alert.condition) ? (
                        <Zap className="w-4 h-4 text-amber-500" />
                      ) : (
                        <Activity className="w-4 h-4 text-slate-400" />
                      )}
                      <div>
                        <span className="text-sm font-medium text-slate-900">
                          {alert.metric_name}
                        </span>
                        <span className="text-sm text-slate-500 ml-2">
                          {CONDITION_LABELS[alert.condition]}
                          {alert.threshold !== null && ` ${alert.threshold}`}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Toggle
                        checked={alert.is_active}
                        onChange={checked => handleToggleMetricAlert(alert.id, checked)}
                      />
                      <button
                        type="button"
                        onClick={() => handleDeleteMetricAlert(alert.id)}
                        className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Channel Overrides Section */}
          <div className="p-6 space-y-4 border-b border-slate-200">
            <div className="flex items-center gap-2 text-slate-700 mb-3">
              <MessageSquare className="w-4 h-4" />
              <span className="font-medium">Channel Overrides</span>
            </div>
            <p className="text-xs text-slate-500 -mt-2 mb-4">
              Override your team's default notification channels for this app only. Leave empty to
              use team defaults.
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Slack Channel Override
              </label>
              <input
                type="text"
                value={slackChannelOverride}
                onChange={e => setSlackChannelOverride(e.target.value)}
                placeholder="#app-alerts"
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">
                Send alerts to a different Slack channel (e.g., #frontend-errors)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Webhook URL Override
              </label>
              <input
                type="url"
                value={webhookUrlOverride}
                onChange={e => setWebhookUrlOverride(e.target.value)}
                placeholder="https://your-webhook.com/alerts"
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">
                Send alerts to a custom webhook endpoint for this app
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Discord Webhook Override
              </label>
              <input
                type="url"
                value={discordWebhookOverride}
                onChange={e => setDiscordWebhookOverride(e.target.value)}
                placeholder="https://discord.com/api/webhooks/..."
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">
                Send alerts to a different Discord webhook for this app
              </p>
            </div>
          </div>

          {/* Report Settings Section */}
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-slate-700 mb-3">
              <FileText className="w-4 h-4" />
              <span className="font-medium">Weekly Report</span>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Include in weekly report
              </label>
              <p className="text-xs text-slate-500 mb-3">
                Only include errors from selected environments in your weekly digest
              </p>
              <div className="flex flex-wrap gap-2">
                {availableEnvironments.map(env => (
                  <button
                    key={env}
                    type="button"
                    onClick={() =>
                      toggleEnvironment(env, reportEnvironments, setReportEnvironments)
                    }
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      reportEnvironments.includes(env)
                        ? 'bg-accent-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {env}
                  </button>
                ))}
              </div>
              {reportEnvironments.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  No environments selected - this app will be excluded from weekly reports
                </p>
              )}
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!name.trim() || isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}
